// src/pages/Main.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, matchPath } from "react-router-dom";
import { setOn403Handler, getBillingStatus } from "../services/server";
import { useAuth } from "../services/AuthContext";
import NoAccess from "./components/NoAccess";
import BillingSetupModal from "./components/BillingSetupModal";
import TrialBanner from "./components/TrialBanner";
import {
  Menu,
  House,
  LayoutGrid,
  LayoutDashboard,
  Calculator,
  FileText,
  FileSearch,
  FolderOpen,
  FolderKanban,
  DollarSign,
  Network,
  History,
  Compass,
  UsersRound,
  Settings,
  Users,
  CircleDashed,
} from "lucide-react";
import "./css/main.css";
import { makeCompanyNav } from "./nav";

/* ─── Koncern breadcrumb ──────────────────────────────── */

// Module-level cache so we don't refetch on every nav
const _koncernCtxCache = {};

function KoncernBreadcrumb({ slug }) {
  const [ctx, setCtx] = useState(_koncernCtxCache[slug] ?? null);

  useEffect(() => {
    if (!slug || _koncernCtxCache[slug] !== undefined) return;
    fetch(`/api/users/workspaces/${encodeURIComponent(slug)}/koncern-context/`, {
      credentials: "include",
    }).then(async (res) => {
      if (res.status === 204) {
        _koncernCtxCache[slug] = null;
        return;
      }
      const data = await res.json().catch(() => null);
      _koncernCtxCache[slug] = data || null;
      setCtx(_koncernCtxCache[slug]);
    }).catch(() => {
      _koncernCtxCache[slug] = null;
    });
  }, [slug]);

  if (!ctx) return null;

  // Determine the koncern link target and the company label
  const koncernTo = `/koncern/${ctx.parent_orgnr}`;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      // Escape main-content's 24px top / 28px side padding, then re-add our own
      margin: "-24px -28px 20px -28px",
      padding: "8px 28px 7px",
      borderBottom: "1px solid #ebebeb",
      background: "#fafafa",
      fontSize: "12px",
      color: "#6b7280",
    }}>
      <Network size={12} strokeWidth={2.2} style={{ color: "#9ca3af" }} />
      <Link
        to={koncernTo}
        style={{ color: "#6b7280", textDecoration: "none", fontWeight: 500 }}
        onMouseOver={(e) => { e.currentTarget.style.color = "#111827"; }}
        onMouseOut={(e) => { e.currentTarget.style.color = "#6b7280"; }}
      >
        Koncern
      </Link>
      <span style={{ color: "#d1d5db" }}>›</span>
      <span style={{ color: "#374151", fontWeight: 500 }}>{ctx.company_name}</span>
      {ctx.is_subsidiary && ctx.parent_name && (
        <span style={{ color: "#9ca3af", fontSize: "11px" }}>({ctx.parent_name})</span>
      )}
    </div>
  );
}

/* ─── Main layout ─────────────────────────────────────── */

function Main({
  navItems,
  children,
  className = "",
  me = null,
}) {
  const location = useLocation();
  // Drawer is open by default. Persisted per-browser so power-users who
  // collapse it keep it collapsed; stays in localStorage only (not synced
  // server-side) since it's purely a per-device visual preference.
  const [isOpen, setIsOpenRaw] = useState(() => {
    try {
      const stored = localStorage.getItem("main_sidebar_open");
      return stored === null ? true : stored === "1";
    } catch {
      return true;
    }
  });
  const setIsOpen = useCallback((next) => {
    setIsOpenRaw((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      try { localStorage.setItem("main_sidebar_open", value ? "1" : "0"); } catch {}
      return value;
    });
  }, []);
  const [accessDenied, setAccessDenied] = useState(false);

  // Reset on navigation
  useEffect(() => {
    setAccessDenied(false);
  }, [location.pathname]);

  // Register global 403 handler
  useEffect(() => {
    setOn403Handler(() => setAccessDenied(true));
    return () => setOn403Handler(null);
  }, []);

  // Extract company slug (orgnr) from URL for breadcrumb
  // Only extract for workspace-scoped routes; ignore static/global routes
  const currentSlug = useMemo(() => {
    const WORKSPACE_SECTIONS = new Set([
      "dashboard", "pipeline", "hitta-kopare", "tasks", "notes", "activity", "valuation", "pm", "duediligence",
      "dataroom", "koncern", "logg", "finance", "workspace-settings", "settings",
    ]);
    const m = matchPath("/:section/:slug", location.pathname);
    if (!m) return null;
    const { section, slug } = m.params;
    if (!WORKSPACE_SECTIONS.has(section)) return null;
    // Accept either a 6-12 digit orgnr (legacy URLs) or a workspace slug
    // (letters, digits, dashes — slugify output + random hex token).
    if (!/^[a-z0-9-]{3,}$/i.test(slug)) return null;
    return slug;
  }, [location.pathname]);

  const globalNav = me?.global_nav || {};
  const workspaceNav = me?.workspace_nav || {};

  const hasSlugInUrl = useMemo(() => {
    const slugRoutes = [
      "/dashboard/:slug",
      "/dashboard/:company",
      "/hitta-kopare/:company",
      "/tasks/:company",
      "/notes/:company",
      "/activity/:company",
      "/pipeline/:slug",
      "/pipeline/:company",
      "/valuation/:slug",
      "/valuation/:company",
      "/pm/:slug",
      "/pm/:company",
      "/duediligence/:slug",
      "/duediligence/:company",
      "/dataroom/:slug",
      "/dataroom/:company",
      "/koncern/:slug",
      "/koncern/:company",
      "/logg/:slug",
      "/logg/:company",
      "/finance/:slug",
      "/finance/:company",
      "/company/:company",
      "/workspace-settings/:slug",
      "/workspace-settings/:company",
      "/company-settings/:slug",
      "/company-settings/:company",
      "/settings/:slug",
      "/settings/:company",
    ];

    return slugRoutes.some((pattern) =>
      matchPath({ path: pattern, end: true }, location.pathname)
    );
  }, [location.pathname]);

  const fallbackItems = useMemo(() => makeCompanyNav(null), []);

  const sourceItems = useMemo(() => {
    if (Array.isArray(navItems) && navItems.length) {
      return navItems;
    }
    return fallbackItems;
  }, [navItems, fallbackItems]);

  const items = useMemo(() => {
    const slugPrefixes = [
      "/dashboard/",
      "/hitta-kopare/",
      "/tasks/",
      "/notes/",
      "/activity/",
      "/pipeline/",
      "/valuation/",
      "/pm/",
      "/duediligence/",
      "/dataroom/",
      "/koncern/",
      "/logg/",
      "/finance/",
      "/company/",
      "/workspace-settings/",
      "/company-settings/",
    ];

    const isWorkspaceSettingsItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();

      return (
        to.startsWith("/workspace-settings/") ||
        to.startsWith("/company-settings/") ||
        (to.startsWith("/settings/") && to !== "/settings/" && to !== "/settings") ||
        label === "workspace settings" ||
        label === "company settings" ||
        label === "deltagare och inställningar"
      );
    };

    const isMainSettingsItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();

      return (
        ((to === "/settings/" || to === "/settings") &&
          !isWorkspaceSettingsItem(it)) ||
        ((label === "settings" || label === "kontoinställningar") &&
          !isWorkspaceSettingsItem(it))
      );
    };

    const isProjectsItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();

      return (
        to === "/projects/" ||
        to === "/projects" ||
        label === "projects" ||
        label === "project"
      );
    };

    const isStartItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();

      return (
        to === "/" ||
        to === "/start" ||
        to === "/start/" ||
        to === "/check" ||
        to === "/check/" ||
        label === "start"
      );
    };

    const isCreateItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();

      return (
        label === "create" ||
        label === "new" ||
        label === "new project" ||
        label === "new workspace" ||
        label === "create workspace" ||
        label === "create project" ||
        to.includes("/create") ||
        to.includes("/new")
      );
    };

    const isDashboardItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return to.startsWith("/dashboard/") || label === "dashboard" || label === "översikt";
    };

    const isValuationItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return to.startsWith("/valuation/") || label === "värdering";
    };

    const isPmItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return to.startsWith("/pm/") || label === "pm studio";
    };

    const isDdItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return to.startsWith("/duediligence/") || label === "due diligence";
    };

    const isDataroomItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return to.startsWith("/dataroom/") || label === "datarum";
    };

    const isKoncernItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return to.startsWith("/koncern/") || label === "koncern";
    };

    const isLoggItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return to.startsWith("/logg/") || label === "logg";
    };

    const isProspectItem = (it) => {
      const to = String(it.to ?? it.href ?? "#").toLowerCase();
      const label = String(it.label ?? it.title ?? "").toLowerCase();
      return (
        to.startsWith("/prospect") ||
        to.startsWith("/prospekt") ||
        label === "prospektering" ||
        label === "prospect" ||
        label === "prospects"
      );
    };

    return sourceItems
      .filter(Boolean)
      .filter((it) => {
        const to = String(it.to ?? it.href ?? "#");
        const isSlugRoute = slugPrefixes.some((prefix) => to.startsWith(prefix));
        const workspaceSetting = isWorkspaceSettingsItem(it);

        if (!hasSlugInUrl && workspaceSetting) return false;
        if (isSlugRoute && !hasSlugInUrl) return false;

        if (isStartItem(it) && globalNav.show_start === false) return false;
        if (isProjectsItem(it) && globalNav.show_projects === false) return false;
        if (isMainSettingsItem(it) && globalNav.show_broker_settings === false) return false;
        if (isCreateItem(it) && globalNav.show_start === false) return false;
        // Prospekt-sökning (SCB + BolagsAPI prospecting) is a broker tool.
        // Sellers and buyers never use it, so hide the nav entry for them.
        if (isProspectItem(it) && !globalNav.is_broker) return false;

        if (isDashboardItem(it) && workspaceNav.dashboard === false) return false;
        if (isValuationItem(it) && workspaceNav.valuation === false) return false;
        if (isPmItem(it) && workspaceNav.pm_studio === false) return false;
        if (isDdItem(it) && workspaceNav.due_diligence === false) return false;
        if (isDataroomItem(it) && workspaceNav.dataroom === false) return false;
        if (isKoncernItem(it) && workspaceNav.koncern === false) return false;
        if (isLoggItem(it) && workspaceNav.logg === false) return false;
        if (workspaceSetting && workspaceNav.workspace_settings === false) return false;

        return true;
      })
      .map((it) => ({
        label: String(it.label ?? it.title ?? "Untitled"),
        to: String(it.to ?? it.href ?? "#"),
        disabled: Boolean(it.disabled),
        exact: Boolean(it.exact || it.end),
        icon: it.icon ?? null,
      }));
  }, [sourceItems, hasSlugInUrl, globalNav, workspaceNav]);

  const itemsWithActive = useMemo(() => {
    const path = location.pathname;

    return items.map((it) => {
      const isActive = it.label === "Tasks & anteckningar" && path.startsWith("/notes/")
        ? true
        : it.exact
        ? path === it.to
        : it.to !== "#" && (path === it.to || path.startsWith(it.to + "/"));

      return { ...it, isActive };
    });
  }, [items, location.pathname]);

  const orderedItems = useMemo(() => {
    const getItem = (label) =>
      itemsWithActive.find((it) => it.label.toLowerCase() === label.toLowerCase());

    const dashboard = getItem("Översikt");
    const pipeline = getItem("Hantera köpare");
    const buyerSearch = getItem("Hitta köpare");
    const workspaceTools = getItem("Tasks & anteckningar");
    const activity = getItem("Aktivitet");
    const valuation = getItem("Värdering");
    const finance = getItem("Finans");
    const pmStudio = getItem("PM Studio");
    const dueDiligence = getItem("Due diligence");
    const datarum = getItem("Datarum");
    const koncern = getItem("Koncern");
    const logg = getItem("Logg");
    const prospektering = getItem("Prospektering");
    const workspaceSettings = getItem("Deltagare och inställningar");

    const start = getItem("Start");
    const projekt = getItem("Projekt");
    const settings = getItem("Kontoinställningar");

    const ordered = [
      // Top cluster — workspace essentials stay visible above Mer.
      // Prospektering, Deltagare-inställningar finns kvar men hamnar
      // under "Mer (N)" eftersom dom inte är i PRIMARY_NAV_LABELS.
      dashboard,
      pipeline,
      buyerSearch,
      workspaceTools,
      activity,
      valuation,
      dueDiligence,
      pmStudio,
      // Secondary (collapses into "Mer" toggle)
      datarum,
      finance,
      koncern,
      logg,
      prospektering,
      workspaceSettings,
      // Bottom cluster (global nav)
      start,
      projekt,
      settings,
    ].filter(Boolean);

    const leftovers = itemsWithActive.filter(
      (it) => !ordered.some((picked) => picked.to === it.to && picked.label === it.label)
    );

    return [...ordered, ...leftovers];
  }, [itemsWithActive]);

  const topClusterItems = useMemo(() => {
    return orderedItems.filter(
      (it) =>
        it.label !== "Start" &&
        it.label !== "Projekt" &&
        it.label !== "Kontoinställningar"
    );
  }, [orderedItems]);

  const bottomClusterItems = useMemo(() => {
    return orderedItems.filter(
      (it) =>
        it.label === "Start" ||
        it.label === "Projekt" ||
        it.label === "Kontoinställningar"
    );
  }, [orderedItems]);

  // Split workspace nav into a small, always-visible primary set and a
  // collapsed "Mer"-group. Default to showing only essentials so new
  // brokers aren't drowning in 11 nav items the first time they open a
  // workspace. Power-users expand "Mer" once and the choice sticks.
  const PRIMARY_NAV_LABELS = useMemo(
    () => new Set(["Översikt", "Hantera köpare", "Hitta köpare", "Tasks & anteckningar", "Aktivitet", "Värdering", "Due diligence", "PM Studio"]),
    [],
  );
  const [moreExpanded, setMoreExpanded] = useState(() => {
    try { return localStorage.getItem("broker_nav_more_expanded") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("broker_nav_more_expanded", moreExpanded ? "1" : "0"); } catch {}
  }, [moreExpanded]);

  const primaryItems = useMemo(
    () => topClusterItems.filter((it) => PRIMARY_NAV_LABELS.has(it.label)),
    [topClusterItems, PRIMARY_NAV_LABELS],
  );
  const secondaryItems = useMemo(
    () => topClusterItems.filter((it) => !PRIMARY_NAV_LABELS.has(it.label)),
    [topClusterItems, PRIMARY_NAV_LABELS],
  );

  return (
    <div className={`main-root ${isOpen ? "main-root--open" : ""}`}>
      <aside className="main-sidebar">
        <div className="main-sidebar-top">
          <div className="main-brand-wrap">
            <Link to="/start/" className="main-logo-link" aria-label="Go to start">
              <img src="/logo192.png" alt="" className="main-logo-img" />
            </Link>

            {!isOpen && (
              <button
                type="button"
                className="main-brand-hover-toggle"
                aria-label="Expand sidebar"
                onClick={() => setIsOpen(true)}
              >
                <Menu size={16} strokeWidth={2.2} />
              </button>
            )}
          </div>

          {isOpen && (
            <button
              type="button"
              className="main-toggle"
              aria-label="Collapse sidebar"
              onClick={() => setIsOpen(false)}
            >
              <Menu size={16} strokeWidth={2.2} />
            </button>
          )}
        </div>

        <nav className="main-nav" aria-label="Navigation">
          <div className="main-nav-top">
            {primaryItems.map((it) => (
              <NavLinkItem key={`${it.label}:${it.to}`} item={it} isOpen={isOpen} />
            ))}

            {secondaryItems.length > 0 && (
              <>
                <button
                  type="button"
                  className="main-nav-link"
                  onClick={() => setMoreExpanded((v) => !v)}
                  aria-expanded={moreExpanded}
                  style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                    cursor: "pointer",
                    color: "#78716c",
                    fontSize: 13,
                  }}
                >
                  <span className="main-nav-ic">{moreExpanded ? "▾" : "▸"}</span>
                  {isOpen && (
                    <span className="main-nav-label">
                      Mer {moreExpanded ? "" : `(${secondaryItems.length})`}
                    </span>
                  )}
                  {!isOpen && <span className="main-nav-tooltip">Mer ({secondaryItems.length})</span>}
                </button>
                {moreExpanded && secondaryItems.map((it) => (
                  <NavLinkItem key={`${it.label}:${it.to}`} item={it} isOpen={isOpen} />
                ))}
              </>
            )}
          </div>

          <div className="main-nav-bottom">
            {bottomClusterItems.map((it) => (
              <NavLinkItem key={`${it.label}:${it.to}`} item={it} isOpen={isOpen} />
            ))}
          </div>
        </nav>
      </aside>

      <main className={`main-content ${className}`.trim()}>
        <TrialBanner />
        {currentSlug && !accessDenied && <KoncernBreadcrumb slug={currentSlug} />}
        {accessDenied ? <NoAccess /> : children}
      </main>
      <BillingSetupGate />
    </div>
  );
}

/**
 * Billing-setup popup gate. Mounts globally inside Main so the modal
 * surfaces on the FIRST page the admin lands on after login (and only
 * once per session per firm).
 *
 * Triggers:
 *   1. On mount — if `getBillingStatus()` returns has_payment_method=false
 *      AND the user is a billing admin (the endpoint 403s otherwise; we
 *      treat 403 as "not an admin → skip popup").
 *   2. Via the custom event `billing:open-setup` — dispatched by
 *      `ActivateWorkspaceButton` when broker tries to activate without
 *      a card on file.
 *
 * Skips repeat fetches per browser session via sessionStorage so
 * navigating between pages doesn't re-trigger the popup.
 */
function BillingSetupGate() {
  const auth = useAuth();
  const user = auth?.user;
  const [open, setOpen] = useState(false);
  // "missing" | "expired" — controls the modal heading + copy.
  const [reason, setReason] = useState("missing");
  // "card" | "invite" — Stripe Checkout return URL sets this to "invite"
  // so the popup picks up right where it left off.
  const [initialStep, setInitialStep] = useState("card");

  // Return-from-Stripe-Checkout handler. Stripe redirects to
  // `<current path>?billing_setup=invite` after the card is saved; we
  // detect it, open the modal at the invite step, and strip the query
  // so a refresh doesn't re-trigger.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing_setup") === "invite") {
      setReason("missing");
      setInitialStep("invite");
      setOpen(true);
      // Clear the dismissal flag so this counts as a fresh open.
      try { sessionStorage.removeItem("billing:setup-dismissed"); } catch (_e) { /* noop */ }
      params.delete("billing_setup");
      const next = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState(null, "", next);
      // Notify other components (e.g. ActivateWorkspaceButton) so the
      // activate flow can auto-retry now that the card has been saved.
      window.dispatchEvent(new CustomEvent("billing:setup-succeeded"));
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem("billing:setup-dismissed") === "1") return;

    let cancelled = false;
    getBillingStatus()
      .then((status) => {
        if (cancelled) return;
        if (!status) return;
        // Qada pricing-model: self_serve/team-workspaces fakturerar
        // via Stripe send_invoice (14 dagar netto) — inget kort krävs
        // för att workspacen ska funka. billing_gate_required=false
        // → skippa popup helt. broker_saas-workspaces behöver kort
        // för off_session-charges → visa popup som förut.
        if (status.billing_gate_required === false) return;
        if (status.has_payment_method === false) {
          setReason("missing");
          setInitialStep("card");
          setOpen(true);
        } else if (status.card_expired === true) {
          setReason("expired");
          setInitialStep("card");
          setOpen(true);
        }
      })
      .catch(() => {
        // 403 (not an admin) or network error — silently skip.
      });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    // ActivateWorkspaceButton dispatches this event with detail.reason
    // set to "missing" | "expired" so the popup heading matches the
    // specific case.
    function onOpen(ev) {
      const r = ev?.detail?.reason;
      if (r === "expired") setReason("expired"); else setReason("missing");
      setInitialStep("card");
      setOpen(true);
    }
    window.addEventListener("billing:open-setup", onOpen);
    return () => window.removeEventListener("billing:open-setup", onOpen);
  }, []);

  function handleClose() {
    setOpen(false);
    sessionStorage.setItem("billing:setup-dismissed", "1");
  }

  function handleSuccess() {
    window.dispatchEvent(new CustomEvent("billing:setup-succeeded"));
  }

  return (
    <BillingSetupModal
      open={open}
      reason={reason}
      initialStep={initialStep}
      onClose={handleClose}
      onSuccess={handleSuccess}
    />
  );
}

const NavLinkItem = React.memo(function NavLinkItem({ item, isOpen }) {
  const cls = "main-nav-link" + (item.isActive ? " main-nav-link--active" : "");

  const content = (
    <>
      <span className="main-nav-ic">{renderIcon(item.label, item.icon)}</span>
      {isOpen && <span className="main-nav-label">{item.label}</span>}
      {!isOpen && <span className="main-nav-tooltip">{item.label}</span>}
    </>
  );

  if (item.disabled || item.to === "#") {
    return (
      <div className={cls} aria-disabled="true">
        {content}
      </div>
    );
  }

  return (
    <Link className={cls} to={item.to}>
      {content}
    </Link>
  );
});

function renderIcon(label, icon) {
  if (React.isValidElement(icon)) return icon;
  if (icon) return icon;

  switch (label) {
    case "Start":
      return <House size={17} strokeWidth={2.1} />;
    case "Dashboard":
    case "Översikt":
      return <LayoutDashboard size={17} strokeWidth={2.1} />;
    case "Pipeline":
    case "Köpare":
      return <Users size={17} strokeWidth={2.1} />;
    case "Värdering":
      return <Calculator size={17} strokeWidth={2.1} />;
    case "PM Studio":
      return <FileText size={17} strokeWidth={2.1} />;
    case "Due diligence":
      return <FileSearch size={17} strokeWidth={2.1} />;
    case "Datarum":
      return <FolderOpen size={17} strokeWidth={2.1} />;
    case "Finans":
      return <DollarSign size={17} strokeWidth={2.1} />;
    case "Logg":
      return <History size={17} strokeWidth={2.1} />;
    case "Projekt":
      return <FolderKanban size={17} strokeWidth={2.1} />;
    case "Workspace Settings":
    case "Deltagare och inställningar":
      return <UsersRound size={17} strokeWidth={2.1} />;
    case "Settings":
    case "Kontoinställningar":
      return <Settings size={17} strokeWidth={2.1} />;
    case "Koncern":
      return <Network size={17} strokeWidth={2.1} />;
    case "Prospektering":
      return <Compass size={17} strokeWidth={2.1} />;
    default:
      return <CircleDashed size={17} strokeWidth={2.1} />;
  }
}

export default React.memo(Main);
